import {
  AuthBadRequestError,
  decideRecoveryMethod,
} from '@creditas-lab/auth-sdk'
import { useTreatments } from '@splitsoftware/splitio-react'
import { useCallback, useEffect, useState } from 'react'
import { useDebouncedCallback } from 'use-debounce'
import { SplitIOTreatments } from '~/constants/split-io'
import { useFieldPageContext } from '~components/FieldPage'
import { URLS, useRoutesNavigateContext } from '~components/RoutesNavigate'
import { useActions, useState as useStateOvermind } from '~context'
import {
  dispatchClickToSignup,
  dispatchErrorLoginPasswordCpfEvent,
  dispatchRequestCreatePasswordEvent,
  dispatchUserDoesNotExistPasswordCpfEvent,
  dispatchUserExistPasswordCpfEvent,
} from '~services/analyticsEvents'
import { checkExistingUser } from '~services/customerVerifier'
import { formatCPF, validateCPF } from '~validators/cpf'

const usePasswordIdentify = () => {
  const [errorMessage, setErrorMessage] = useState('')
  const { goPrev, current, navigate } = useRoutesNavigateContext()
  const { password } = useStateOvermind()
  const actions = useActions()
  const [document, setDocument] = useState(password.username)

  const treatments = useTreatments([SplitIOTreatments.isSelectClaimEnabled])
  const { treatment: isSelectClaimEnabled } = treatments[
    SplitIOTreatments.isSelectClaimEnabled
  ]

  const onBack = useCallback(() => {
    goPrev()
  }, [current])

  const redirectToCreatePassword = (username, recoveryMethod) => {
    dispatchRequestCreatePasswordEvent(recoveryMethod)
    navigate(URLS.createPassEmailSend())
  }

  const goToLoginOrCreatePassword = (hasPassword, username, recoveryMethod) => {
    if (!hasPassword) {
      return redirectToCreatePassword(username, recoveryMethod)
    }

    return navigate(
      isSelectClaimEnabled === 'on'
        ? URLS.password.selectClaim.path()
        : URLS.password.enter.path(),
    )
  }

  const handleValidCustomer = (
    { hasAccount, hasPassword, hasEmail, hasCellphone },
    username,
  ) => {
    if (hasAccount) {
      return goToLoginOrCreatePassword(
        hasPassword,
        username,
        decideRecoveryMethod(hasEmail, hasCellphone),
      )
    }

    return null
  }

  const handleCreateAccountClick = useCallback(event => {
    event.preventDefault()
    dispatchClickToSignup()

    navigate(URLS.signup.identify.path())
  }, [])

  const handleInvalidCustomer = error => {
    setErrorMessage('pages:password.identify.errors.error_message')

    if (error instanceof AuthBadRequestError) {
      dispatchUserDoesNotExistPasswordCpfEvent(error.message)
    } else {
      dispatchErrorLoginPasswordCpfEvent(error.message)
    }
  }

  const onSubmit = useCallback(
    async event => {
      event.preventDefault()
      try {
        actions.password.setUsername(document)
        const existingUserData = await checkExistingUser(document)
        dispatchUserExistPasswordCpfEvent()
        await handleValidCustomer(existingUserData, document)
      } catch (error) {
        handleInvalidCustomer(error)
      }
    },
    [document],
  )

  const { toFocus, toValid, loading, handleSubmit } = useFieldPageContext({
    onBack,
    onSubmit,
  })

  const validateInput = value => {
    setErrorMessage('')

    if (validateCPF(value)) {
      toValid()
    } else {
      toFocus()
    }
  }

  const handleDebouncedValidation = useDebouncedCallback(value => {
    if (!value) {
      setErrorMessage('')
      return
    }

    if (!validateCPF(value)) {
      toFocus()
      setErrorMessage('pages:errors.invalid_document')
      return
    }

    toValid()
  }, 800)

  const handlerInputChange = useCallback(event => {
    const { value } = event.target

    setDocument(formatCPF(value))
    validateInput(value)
    handleDebouncedValidation(value)
  }, [])

  useEffect(() => {
    if (document && document !== '') {
      validateInput(document)
    }
  }, [])

  return {
    handlerInputChange,
    handleDebouncedValidation,
    handleCreateAccountClick,
    handleSubmit,
    loading,
    document,
    errorMessage,
  }
}

export { usePasswordIdentify }
